import { computed, Injectable, signal } from '@angular/core';

import { UserAccountRoutes, UserAuthRoutes } from '@ripeurs/common/types/gateway';

import { catchError, Observable, of, switchMap } from 'rxjs';

import { LocalStorage } from '~app/common/decorator/local-storage.decorator';
import { ApiV2Service } from '~app/services/api-v2.service';
import { UtService } from '~app/services/ut.service';
import { UserPermissions } from '~app/types/user';
import { RegionType } from '~type/service/geo/region';

import type { DatalakeUser } from '@ripeurs/common/types/service';


type UserMe = UserAccountRoutes.UserMe['user'] & { email: string; firstName: string; id: string; lastName: string; phone: string; };
type UserData = (UserMe & { 'meta.regions'?: RegionType.Region }) | null;

@Injectable({ providedIn: 'root' })
export class UserService {
	public readonly user;
	public readonly region;
	public readonly logged = computed(() => this._user() !== null);
	@LocalStorage('user_token') private token: string | null | undefined;
	private permissionAccepted = [UserPermissions.OPERATOR_UTILITY];
	private readonly _user = signal<UserData>(null);
	private readonly _region = signal<RegionType.Region | null>(null);

	constructor(
		private readonly _api: ApiV2Service,
		private readonly _ut: UtService,
	) {
		this.user = this._user.asReadonly();
		this.region = this._region.asReadonly();
	}

	public getToken(): string | null | undefined { return this.token; }

	public getData(): UserData { return this._user(); }

	public getRegion(): RegionType.Region | null { return this._region(); }

	public loggedGetRegion(): RegionType.Region {
		const region = this._region();
		if (region === null) { throw new Error('User not logged'); }
		return region;
	}

	public isLogged(): boolean { return this._user() !== null; }

	public login(
		email: string,
		password: string,
	): Observable<boolean> {
		// A sign-in request must never reuse a stale token. It could otherwise
		// authenticate the request independently of the submitted credentials.
		this.logout();
		return this._api.request<UserAuthRoutes.PostSignin.Def>(
			UserAuthRoutes.PostSignin.config,
			{ body: { email, password } },
		).pipe(
			switchMap(this.handleLoginResponse.bind(this)),
			catchError(() => {
				this.logout();
				return of(false);
			}),
		);
	}

	public loginToken(): any {
		return this._api.request<UserAccountRoutes.GetMe.Def>(
			UserAccountRoutes.GetMe.config,
			{},
		).pipe(
			switchMap(this.handleLoginResponse.bind(this)),
			catchError(() => {
				this.logout();
				return of(false);
			}),
		);
	}

	public logout(): void {
		this.setData(null);
		this.token = null;
	}

	private setData(user: any | null): void {
		if (user !== null) {
			// @TODO Temporaire à importer du serveur
			if (user.meta === undefined) { user.meta = { regions: [] }; }
			if (!user.meta?.regions?.length) { user.meta.regions = this._ut.getMetaRegions(); }
			this._region.set(user.meta.regions.find(({ id }: RegionType.Region) => id === user.region) || user.meta.regions[0]);
		} else {
			this._region.set(null);
		}
		this._user.set(user);
	}

	private handleLoginResponse(
		response: UserAuthRoutes.PostSignin.Def['response'] | UserAccountRoutes.GetMe.Def['response'],
	): Observable<boolean> {
		if (response.code !== 'OK' || !this.checkPermissions(response.data.user.permissions)) {
			this.logout();
			return of(false);
		}

		this.setData(this.normalizeUserData(response.data.user));
		if ('token' in response.data) { this.token = response.data.token; }
		return of(true);
	}

	private checkPermissions(permissions: string[]): boolean {
		return permissions.some((permission) => this.permissionAccepted.includes(<UserPermissions>permission));
	}

	private normalizeUserData(user: Partial<DatalakeUser.User> | UserMe): Partial<UserMe> {
		return {
			email: (<UserMe>user).email || (<DatalakeUser.User>user).data.email!,
			firstName: (<UserMe>user).firstName || (<DatalakeUser.User>user).data.firstName!,
			lastName: (<UserMe>user).lastName || (<DatalakeUser.User>user).data.lastName!,
			permissions : user.permissions!,
			phone: (<UserMe>user).phone || (<DatalakeUser.User>user).data.phoneNumber!,
			region : user.region,
			userId : (<UserMe>user).id || (<DatalakeUser.User>user).userId,
		};
	}
}
